import { useState } from 'react';
import { Stack } from '@mui/material';
import {
  CasBaseDialog,
  CasChip,
  CasDisplayField,
  CasLoaderWithBackdrop,
  CasStackRow,
  CasTable,
} from 'src/compass/shared/components';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';
import { useRetrieveDetailsById } from 'src/compass/shared/hooks/reactQuery/useRetrieveDetailsById';
import { RenderContactMethods } from 'src/compass/application';
import HubspotLink from 'src/compass/application/entityMaintenance/HubspotLink';
import CustomerDialog from './CustomerDialog';
import InactiveChip from './InactiveChip';

interface IProps {
  id: number;
  onClose: VoidFunction;
  // A customer opened from the list changed something its Customers tab row shows.
  onCustomerUpdated: VoidFunction;
}

/*
  What the Customers page search box opens for a contact that isn't attached to exactly one
  customer - one that is opens that customer's dialog instead. The contact's own details up top,
  then the customers it is attached to, any of which opens its customer dialog.
*/
export default function ContactDialog({ id, onClose, onCustomerUpdated }: IProps) {
  const [customerId, setCustomerId] = useState<number | null>(null);

  const { data, isLoading, isError } = useRetrieveDetailsById({
    entity: 'Utilities/CustomerSearch/Contact',
    id,
  });

  if (isLoading) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error('Error retrieving data from the server.');

  return (
    <CasBaseDialog
      open
      fullWidth
      maxWidth="lg"
      onClose={onClose}
      showCloseIcon
      title={
        <CasStackRow alignItems="center">
          <HubspotLink id={data.id} recordPath="record/0-1" />
          <span>{`Contact: ${data.name}`}</span>
        </CasStackRow>
      }
    >
      <Stack spacing={2}>
        <CasStackRow>
          <CasDisplayField label="Email/Phone" value={<RenderContactMethods contact={data} />} />
          <CasDisplayField
            label="Last Touchpoint"
            value={formatDate(data.lastTouchpointDate, DateFormat.MDY)}
          />
          <CasDisplayField
            label="Last LRP Quote Open"
            value={formatDate(data.lastLRPQuoteOpenedDate, DateFormat.MDY)}
          />
        </CasStackRow>
        <CasTable
          data={{ result: data.customers }}
          noDataMessage="Associated to no customers"
          columns={[
            {
              field: 'name',
              label: 'Customer',
              render: ({ row }: ICasRenderCellProps) => (
                <CasStackRow alignItems="center">
                  <CasLink onClick={() => setCustomerId(row.id)}>{row.name}</CasLink>
                  {row.inactive && <InactiveChip />}
                </CasStackRow>
              ),
            },
            {
              field: 'riskManagerNames',
              label: 'Risk Managers',
              render: ({ row }: ICasRenderCellProps) => (
                <CasStackRow>
                  {row.riskManagerNames?.map((riskManager: string) => (
                    <CasChip key={riskManager} label={riskManager} color="disabled" size="small" />
                  ))}
                </CasStackRow>
              ),
            },
          ]}
        />
      </Stack>
      {customerId && (
        <CustomerDialog
          id={customerId}
          onClose={(dataUpdated?: boolean) => {
            setCustomerId(null);
            if (dataUpdated) onCustomerUpdated();
          }}
        />
      )}
    </CasBaseDialog>
  );
}
